/* ═══════════════════════════════════════════════════════════════════════
   YL-GSAP-CHAMBERS.JS — Supreme Build · Per-Chamber Scroll Choreography
   ═══════════════════════════════════════════════════════════════════════
   Depends on: gsap.min.js + ScrollTrigger.min.js (loaded in theme.liquid)
   Waits for the `yl:gsap-ready` event fired by the global engine.
   Each chamber gets bespoke scroll-triggered animations that enhance
   the archival atmosphere without interfering with content legibility.
   ═══════════════════════════════════════════════════════════════════════ */

(function () {
  'use strict';

  function init() {
    if (typeof gsap === 'undefined' || typeof ScrollTrigger === 'undefined') return;

    /* ── CHAMBER I · SANCTUM ──────────────────────────────────────────
       Cream paper atmosphere. Devanagari name fades up slowly.
       Book cover slides in from left. Commentary paragraphs stagger.
       ─────────────────────────────────────────────────────────────── */
    (function sanctum() {
      var el = document.querySelector('.yl-sanctum');
      if (!el) return;

      // Giant Devanagari character — gentle parallax
      var deva = el.querySelector('.yl-sanctum-name__devanagari');
      if (deva) {
        gsap.fromTo(deva,
          { y: 60, opacity: 0 },
          { y: 0, opacity: 0.22, duration: 1.8, ease: 'power2.out',
            scrollTrigger: { trigger: deva, start: 'top 85%' } });
      }

      // Primary name — scale up from slightly smaller
      var primary = el.querySelector('.yl-sanctum-name__primary');
      if (primary) {
        gsap.from(primary,
          { scale: 0.92, opacity: 0, duration: 1.2, ease: 'power2.out',
            scrollTrigger: { trigger: primary, start: 'top 80%' } });
      }

      // IAST transliteration
      var iast = el.querySelector('.yl-sanctum-name__iast');
      if (iast) {
        gsap.from(iast,
          { y: 20, opacity: 0, duration: 0.8, delay: 0.3, ease: 'power2.out',
            scrollTrigger: { trigger: iast, start: 'top 85%' } });
      }

      // Commentary paragraphs stagger
      var commentary = el.querySelectorAll('.yl-sanctum-name__commentary p');
      if (commentary.length) {
        gsap.from(commentary,
          { y: 30, opacity: 0, stagger: 0.2, duration: 0.9, ease: 'power2.out',
            scrollTrigger: { trigger: commentary[0], start: 'top 80%' } });
      }

      // Book cover — slide in from left
      var cover = el.querySelector('.yl-sanctum-acquisition__cover');
      if (cover) {
        gsap.from(cover,
          { x: -60, opacity: 0, duration: 1.2, ease: 'power3.out',
            scrollTrigger: { trigger: cover, start: 'top 80%' } });
      }

      // Book details — slide in from right with stagger
      var details = el.querySelectorAll('.yl-sanctum-acquisition__details > *');
      if (details.length) {
        gsap.from(details,
          { x: 40, opacity: 0, stagger: 0.12, duration: 0.8, ease: 'power2.out',
            scrollTrigger: { trigger: details[0], start: 'top 80%' } });
      }
    })();

    /* ── CHAMBER II · THE ERASED ──────────────────────────────────────
       War memorial tone. Somber reveals. Pilot records stagger in
       like names being read from a roll call. The gravity well
       number scales up dramatically.
       ─────────────────────────────────────────────────────────────── */
    (function erased() {
      var el = document.querySelector('.yl-chamber-ii');
      if (!el) return;

      // Compiler's Address — paragraphs stagger
      var address = el.querySelectorAll('.yl-chamber-ii__compiler-address p');
      if (address.length) {
        gsap.from(address,
          { y: 25, opacity: 0, stagger: 0.18, duration: 0.9, ease: 'power2.out',
            scrollTrigger: { trigger: address[0], start: 'top 78%' } });
      }

      // Colditz excerpt — left border expands
      var excerpt = el.querySelector('.yl-chamber-ii__excerpt');
      if (excerpt) {
        gsap.from(excerpt,
          { x: -30, opacity: 0, duration: 1.0, ease: 'power2.out',
            scrollTrigger: { trigger: excerpt, start: 'top 80%' } });
      }

      // Film tile — iris open
      var filmTile = el.querySelector('.yl-cii-film');
      if (filmTile) {
        gsap.from(filmTile,
          { clipPath: 'inset(8% 8% 8% 8%)', opacity: 0.5, duration: 1.4, ease: 'power3.out',
            scrollTrigger: { trigger: filmTile, start: 'top 75%' } });
      }

      // Pilot records — stagger like roll call (slow, reverent)
      var pilots = el.querySelectorAll('.yl-chamber-ii__pilot-stack > *');
      if (pilots.length) {
        gsap.from(pilots,
          { y: 40, opacity: 0, stagger: 0.25, duration: 1.0, ease: 'power2.out',
            scrollTrigger: { trigger: pilots[0], start: 'top 80%' } });
      }

      // Gravity well — the "2,500,000" counter
      var gravityFig = el.querySelector('.yl-chamber-ii__gravity-well__figure');
      if (gravityFig) {
        gsap.from(gravityFig,
          { scale: 0.6, opacity: 0, duration: 2.0, ease: 'power4.out',
            scrollTrigger: { trigger: gravityFig, start: 'top 75%' } });
      }

      // Nominal roll entries — stagger in slowly
      var rollEntries = el.querySelectorAll('.yl-chamber-ii__nominal-roll__entry');
      if (rollEntries.length) {
        gsap.from(rollEntries,
          { y: 20, opacity: 0, stagger: 0.08, duration: 0.6, ease: 'power1.out',
            scrollTrigger: { trigger: rollEntries[0], start: 'top 85%' } });
      }

      // Theatre cards stagger
      var theatres = el.querySelectorAll('.yl-chamber-ii__theatre-stack > *');
      if (theatres.length) {
        gsap.from(theatres,
          { y: 30, opacity: 0, stagger: 0.15, duration: 0.8, ease: 'power2.out',
            scrollTrigger: { trigger: theatres[0], start: 'top 80%' } });
      }
    })();

    /* ── CHAMBER III · OPERATIONS ROOM ────────────────────────────────
       Intelligence briefing register. Dossier cards flip in.
       State briefings slide from alternating sides. Signal key
       pulses. Project Yoddha card materializes.
       ─────────────────────────────────────────────────────────────── */
    (function operations() {
      var el = document.querySelector('.yl-chamber-iii');
      if (!el) return;

      // Compiler's address
      var address = el.querySelectorAll('.yl-chamber-iii__compiler-address p');
      if (address.length) {
        gsap.from(address,
          { y: 25, opacity: 0, stagger: 0.15, duration: 0.8, ease: 'power2.out',
            scrollTrigger: { trigger: address[0], start: 'top 80%' } });
      }

      // Dossier cards — stagger with slight rotation (like files being laid on desk)
      var dossiers = el.querySelectorAll('.yl-chamber-iii__dossier-stack > *');
      if (dossiers.length) {
        dossiers.forEach(function (card, i) {
          var rot = (i % 2 === 0) ? -1.5 : 1.5;
          gsap.from(card,
            { y: 50, rotateZ: rot, opacity: 0, duration: 0.9,
              delay: i * 0.12, ease: 'power3.out',
              scrollTrigger: { trigger: card, start: 'top 82%' } });
        });
      }

      // State briefings — alternate slide direction
      var states = el.querySelectorAll('.yl-chamber-iii__state');
      if (states.length) {
        states.forEach(function (state, i) {
          var fromX = (i % 2 === 0) ? -40 : 40;
          gsap.from(state,
            { x: fromX, opacity: 0, duration: 1.0, ease: 'power2.out',
              scrollTrigger: { trigger: state, start: 'top 80%' } });
        });
      }

      // Signal key — scale in with pulse
      var signalKey = el.querySelector('.yl-chamber-iii__signal-key');
      if (signalKey) {
        gsap.from(signalKey,
          { scale: 0.9, opacity: 0, duration: 1.2, ease: 'elastic.out(1, 0.75)',
            scrollTrigger: { trigger: signalKey, start: 'top 80%' } });
      }

      // Project Yoddha card — dramatic reveal
      var pyCard = el.querySelector('.yl-chamber-iii__py-card');
      if (pyCard) {
        gsap.from(pyCard,
          { y: 60, opacity: 0, duration: 1.5, ease: 'power3.out',
            scrollTrigger: { trigger: pyCard, start: 'top 78%' } });
      }

      // Fajr timer
      var fajr = el.querySelector('.yl-chamber-iii__fajr-timer');
      if (fajr) {
        gsap.from(fajr,
          { y: 30, opacity: 0, duration: 1.0, ease: 'power2.out',
            scrollTrigger: { trigger: fajr, start: 'top 85%' } });
      }
    })();

    /* ── CHAMBER IV · THE ARMORY ──────────────────────────────────────
       Artifact showcase. Products appear with golden bloom.
       Ghost grid items materialize one by one.
       ─────────────────────────────────────────────────────────────── */
    (function armory() {
      var el = document.querySelector('.yl-chamber-iv');
      if (!el) return;

      // Prose sections
      var prose = el.querySelectorAll('.yl-chamber-iv__prose p');
      if (prose.length) {
        gsap.from(prose,
          { y: 25, opacity: 0, stagger: 0.15, duration: 0.8, ease: 'power2.out',
            scrollTrigger: { trigger: prose[0], start: 'top 80%' } });
      }

      // Artifact stack — each item scales in with golden bloom effect
      var artifacts = el.querySelectorAll('.yl-chamber-iv__artifact-stack > *');
      if (artifacts.length) {
        artifacts.forEach(function (item, i) {
          gsap.from(item,
            { scale: 0.88, opacity: 0, duration: 1.0, delay: i * 0.15,
              ease: 'power2.out',
              scrollTrigger: { trigger: item, start: 'top 80%' } });
        });
      }

      // Ghost grid — items materialize
      var ghosts = el.querySelectorAll('.yl-chamber-iv__ghost-grid > *');
      if (ghosts.length) {
        gsap.from(ghosts,
          { y: 30, opacity: 0, stagger: 0.1, duration: 0.7, ease: 'power2.out',
            scrollTrigger: { trigger: ghosts[0], start: 'top 82%' } });
      }
    })();

    /* ── CHAMBER V · TRANSMISSION ─────────────────────────────────────
       2126 Bharat signal register. Navy/silver/signal-blue palette.
       Transmission log types in (leverages existing CSS). Cultural
       State table rows appear one by one.
       ─────────────────────────────────────────────────────────────── */
    (function transmission() {
      var el = document.querySelector('.yl-chamber-v');
      if (!el) return;

      // Framing text
      var framing = el.querySelector('.yl-chamber-v__framing');
      if (framing) {
        gsap.from(framing,
          { y: 30, opacity: 0, duration: 1.2, ease: 'power2.out',
            scrollTrigger: { trigger: framing, start: 'top 80%' } });
      }

      // Transmission container — border draws in
      var txContainer = el.querySelector('.yl-chamber-v__tx-container');
      if (txContainer) {
        gsap.from(txContainer,
          { clipPath: 'inset(0 0 100% 0)', duration: 1.5, ease: 'power3.out',
            scrollTrigger: { trigger: txContainer, start: 'top 75%' } });
      }

      // Cultural State table rows
      var rows = el.querySelectorAll('.yl-chamber-v__table tbody tr');
      if (rows.length) {
        gsap.from(rows,
          { x: -20, opacity: 0, stagger: 0.1, duration: 0.6, ease: 'power2.out',
            scrollTrigger: { trigger: rows[0], start: 'top 85%' } });
      }

      // Positioning block
      var positioning = el.querySelector('.yl-chamber-v__positioning');
      if (positioning) {
        gsap.from(positioning,
          { y: 30, opacity: 0, duration: 1.0, ease: 'power2.out',
            scrollTrigger: { trigger: positioning, start: 'top 80%' } });
      }
    })();

    /* ── CHAMBER VI · FILM ARCHIVE ────────────────────────────────────
       Cinema register. Film tiles iris-open. Masthead slides down.
       ─────────────────────────────────────────────────────────────── */
    (function filmArchive() {
      var el = document.querySelector('.yl-film-archive');
      if (!el) return;

      // Masthead
      var masthead = el.querySelector('.yl-film-masthead');
      if (masthead) {
        gsap.from(masthead,
          { y: -30, opacity: 0, duration: 1.0, ease: 'power2.out',
            scrollTrigger: { trigger: masthead, start: 'top 85%' } });
      }

      // Film tiles — iris open effect, staggered
      var tiles = el.querySelectorAll('.yl-film-tile-inner');
      if (tiles.length) {
        tiles.forEach(function (tile, i) {
          gsap.from(tile,
            { clipPath: 'inset(20% 20% 20% 20%)', opacity: 0,
              duration: 1.2, delay: i * 0.15, ease: 'power3.out',
              scrollTrigger: { trigger: tile, start: 'top 80%' } });
        });
      }

      // Film index
      var index = el.querySelector('.yl-film-index');
      if (index) {
        gsap.from(index,
          { y: 40, opacity: 0, duration: 1.0, ease: 'power2.out',
            scrollTrigger: { trigger: index, start: 'top 82%' } });
      }
    })();

    /* ── DISPATCH ──────────────────────────────────────────────────────
       Briefing dispatch. Masthead, form, and framing reveal.
       ─────────────────────────────────────────────────────────────── */
    (function dispatch() {
      var el = document.querySelector('.yl-dispatch-page');
      if (!el) return;

      // Masthead — chapter number and title
      var masthead = el.querySelector('.yl-dispatch-masthead');
      if (masthead) {
        gsap.from(masthead,
          { y: -30, opacity: 0, duration: 1.0, ease: 'power2.out',
            scrollTrigger: { trigger: masthead, start: 'top 85%' } });
      }

      // Framing prose
      var framing = el.querySelector('.yl-dispatch-framing');
      if (framing) {
        gsap.from(framing,
          { y: 25, opacity: 0, duration: 0.9, ease: 'power2.out',
            scrollTrigger: { trigger: framing, start: 'top 82%' } });
      }

      // Form container — reveal
      var form = el.querySelector('.yl-dispatch-form-container');
      if (form) {
        gsap.from(form,
          { y: 40, opacity: 0, duration: 1.2, ease: 'power3.out',
            scrollTrigger: { trigger: form, start: 'top 78%' } });
      }

      // All remaining sections
      var sections = el.querySelectorAll('.yl-dispatch__section, .yl-dispatch-section, [class*="dispatch"] > section');
      if (sections.length) {
        gsap.from(sections,
          { y: 35, opacity: 0, stagger: 0.2, duration: 0.9, ease: 'power2.out',
            scrollTrigger: { trigger: sections[0], start: 'top 80%' } });
      }
    })();

    /* ── READING ROOM ─────────────────────────────────────────────────
       Cream paper atmosphere. Fragment cards reveal like files being
       laid on a table. Section head slides in.
       ─────────────────────────────────────────────────────────────── */
    (function readingRoom() {
      var el = document.querySelector('.yl-reading-room');
      if (!el) return;

      // Section head
      var head = el.querySelector('.yl-rr-section-head');
      if (head) {
        gsap.from(head,
          { y: 30, opacity: 0, duration: 1.0, ease: 'power2.out',
            scrollTrigger: { trigger: head, start: 'top 85%' } });
      }

      // Fragment cells — stagger like files being laid down
      var cells = el.querySelectorAll('.yl-rr-cell');
      if (cells.length) {
        cells.forEach(function (cell, i) {
          var rot = (i % 2 === 0) ? -1 : 1;
          gsap.from(cell,
            { y: 50, rotateZ: rot, opacity: 0, duration: 1.0,
              delay: i * 0.15, ease: 'power3.out',
              scrollTrigger: { trigger: cell, start: 'top 82%' } });
        });
      }
    })();

    /* ── OPERATOR ──────────────────────────────────────────────────────
       Personnel file. Sections slide in alternating from left/right.
       ─────────────────────────────────────────────────────────────── */
    (function operator() {
      var el = document.querySelector('.yl-operator-page');
      if (!el) return;

      // All major sections
      var sections = el.querySelectorAll('[class*="operator__section"], .yl-operator-page > section, .yl-operator-page > div > section');
      if (sections.length) {
        sections.forEach(function (sec, i) {
          var fromX = (i % 2 === 0) ? -30 : 30;
          gsap.from(sec,
            { x: fromX, opacity: 0, duration: 1.0, ease: 'power2.out',
              scrollTrigger: { trigger: sec, start: 'top 82%' } });
        });
      }
    })();

    /* ── PERMANENT SEAL ─────────────────────────────────────────────────
       Legal dossier index. Seal entries reveal like classified docs.
       ─────────────────────────────────────────────────────────────── */
    (function permanentSeal() {
      var el = document.querySelector('.yl-ps-page');
      if (!el) return;

      // Heading
      var heading = el.querySelector('.yl-ps-heading');
      if (heading) {
        gsap.from(heading,
          { y: -20, opacity: 0, duration: 0.9, ease: 'power2.out',
            scrollTrigger: { trigger: heading, start: 'top 85%' } });
      }

      // Subhead
      var subhead = el.querySelector('.yl-ps-subhead');
      if (subhead) {
        gsap.from(subhead,
          { y: 20, opacity: 0, duration: 0.8, delay: 0.2, ease: 'power2.out',
            scrollTrigger: { trigger: subhead, start: 'top 85%' } });
      }

      // Seal entries — stagger like classified documents being revealed
      var entries = el.querySelectorAll('.yl-ps-seal-entry');
      if (entries.length) {
        gsap.from(entries,
          { y: 30, opacity: 0, stagger: 0.15, duration: 0.8, ease: 'power2.out',
            scrollTrigger: { trigger: entries[0], start: 'top 82%' } });
      }
    })();

    /* ── GLOBAL · CLASSIFICATION HEADERS ──────────────────────────────
       Every chamber has a classification header snippet. Animate
       them with a subtle top-down reveal across the site.
       ─────────────────────────────────────────────────────────────── */
    var classHeaders = document.querySelectorAll('.yl-classification-header');
    classHeaders.forEach(function (hdr) {
      gsap.from(hdr,
        { y: -20, opacity: 0, duration: 0.8, ease: 'power2.out',
          scrollTrigger: { trigger: hdr, start: 'top 90%' } });
    });

    /* ── GLOBAL · COLOPHONS ───────────────────────────────────────────
       Every chamber ends with a colophon. Fade up subtly.
       ─────────────────────────────────────────────────────────────── */
    var colophons = document.querySelectorAll('.yl-colophon, [class*="colophon"]');
    colophons.forEach(function (col) {
      gsap.from(col,
        { y: 20, opacity: 0, duration: 0.8, ease: 'power2.out',
          scrollTrigger: { trigger: col, start: 'top 90%' } });
    });

    /* ── GLOBAL · SECTION LABELS ──────────────────────────────────────
       Mono-font section labels across chambers — subtle slide-in.
       ─────────────────────────────────────────────────────────────── */
    var labels = document.querySelectorAll('[class*="__section-label"]');
    labels.forEach(function (lbl) {
      gsap.from(lbl,
        { x: -15, opacity: 0, duration: 0.6, ease: 'power2.out',
          scrollTrigger: { trigger: lbl, start: 'top 88%' } });
    });

    /* ── GLOBAL · BRIDGE / CTA LINKS ──────────────────────────────────
       Inter-chamber navigation links — scale in.
       ─────────────────────────────────────────────────────────────── */
    var bridges = document.querySelectorAll('[class*="bridge-link"], [class*="return-link"]');
    bridges.forEach(function (br) {
      gsap.from(br,
        { scale: 0.9, opacity: 0, duration: 0.8, ease: 'back.out(1.4)',
          scrollTrigger: { trigger: br, start: 'top 88%' } });
    });

    ScrollTrigger.refresh();
  }

  /* Boot: wait for GSAP ready signal */
  if (window.YL_GSAP_READY) {
    init();
  } else {
    document.addEventListener('yl:gsap-ready', init);
  }

})();
